import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, Search, Ban, Trash2, AlertTriangle, CheckCircle, FileText, ShieldCheck, Wand2,
} from 'lucide-react';
import {
  apiGetContacts, apiContactStats, apiContactLists,
  apiImportContacts, apiSuppressContact, apiDeleteContact,
  apiVerifyStats, apiVerifyContacts, apiApplySuggestion,
} from '../../api';
import { Stat, StatusBadge, Field, Empty, formatDate } from './parts';
import styles from './Mailer.module.css';

/**
 * Verdict shown per contact. `unverified` is deliberately quiet — most of a
 * list will be unchecked before the first sweep, and colouring all of it a
 * warning would make the genuinely bad rows invisible.
 */
const VERIFY_TONE = {
  valid: 'badge-high',
  risky: 'badge-medium',
  invalid: 'badge-critical',
  unknown: 'badge-medium',
  unverified: 'badge-low',
};

const VerifyBadge = ({ verification }) => {
  const result = verification?.result || 'unverified';
  return (
    <span className={`badge ${VERIFY_TONE[result] || 'badge-low'}`} title={verification?.reason || ''}>
      {result}
    </span>
  );
};

/**
 * Contacts and CSV import.
 *
 * Import is two-phase on purpose: the file is analysed with dryRun first and
 * nothing is written until the summary has been seen. Committing 400 rows blind
 * is how duplicates, dead addresses and role accounts get into a list, and each
 * of those costs sending reputation later.
 */
const ContactsTab = ({ jwtToken }) => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [lists, setLists] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: 'all', list: '', search: '', verification: 'all' });
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [verifyStats, setVerifyStats] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyRun, setVerifyRun] = useState(null);
  // Set when the operator asks to stop mid-sweep. A ref rather than state
  // because the sweep loop below closes over it and must see the change
  // immediately, not on the next render.
  const cancelVerify = useRef(false);

  const [file, setFile] = useState(null);
  const [listName, setListName] = useState('');
  const [dryRun, setDryRun] = useState(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);
  const loadRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++loadRequest.current;
    setLoading(true);
    setError('');
    try {
      const [contactData, statData, listData, verifyData] = await Promise.all([
        apiGetContacts(jwtToken, { ...filters, page, limit: 50 }),
        apiContactStats(jwtToken),
        apiContactLists(jwtToken),
        apiVerifyStats(jwtToken),
      ]);
      if (requestId !== loadRequest.current) return;
      setContacts(contactData.contacts || []);
      setTotal(contactData.total || 0);
      setPages(contactData.pages || 1);
      setStats(statData);
      setLists(listData.lists || []);
      setVerifyStats(verifyData);
    } catch (e) {
      if (requestId !== loadRequest.current) return;
      setError(e.message || 'Could not load contacts');
    } finally {
      if (requestId === loadRequest.current) setLoading(false);
    }
  }, [jwtToken, filters, page]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (event) => {
    event?.preventDefault();
    setPage(1);
    setFilters((current) => ({ ...current, search: searchInput.trim() }));
  };

  const analyse = async (selected) => {
    setFile(selected);
    setImported(null);
    setDryRun(null);
    setError('');
    setImporting(true);
    try {
      setDryRun(await apiImportContacts(jwtToken, selected, { listName, dryRun: true }));
    } catch (e) {
      setError(e.message || 'Could not read that CSV');
      setFile(null);
    } finally {
      setImporting(false);
    }
  };

  const commit = async () => {
    setImporting(true);
    setError('');
    try {
      const result = await apiImportContacts(jwtToken, file, { listName, dryRun: false });
      setImported(result);
      setDryRun(null);
      setFile(null);
      if (fileInput.current) fileInput.current.value = '';
      setPage(1);
      await load();
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setFile(null);
    setDryRun(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  /**
   * Run a verification sweep, batch by batch.
   *
   * The server refuses to do the whole list in one request, so the loop lives
   * here — which also means progress is visible and the run can be stopped
   * part-way. Every completed batch is already written, so stopping loses
   * nothing: the next run picks up whatever is still unverified.
   */
  const runVerification = async (scope) => {
    setVerifying(true);
    setError('');
    cancelVerify.current = false;

    const tally = { checked: 0, valid: 0, risky: 0, invalid: 0, unknown: 0, samples: [], remaining: 0 };
    setVerifyRun({ ...tally, running: true });

    try {
      let remaining = 1;
      // Batches walk forward by contact id. A "re-verify everything" sweep
      // matches rows it has just written, so without the cursor the loop would
      // never advance past the first batch.
      let after = '';

      while (remaining > 0 && !cancelVerify.current) {
        const batch = await apiVerifyContacts(jwtToken, {
          scope,
          list: filters.list || '',
          limit: 500,
          after,
        });
        after = batch.lastId || after;

        tally.checked += batch.checked;
        tally.valid += batch.valid;
        tally.risky += batch.risky;
        tally.invalid += batch.invalid;
        tally.unknown += batch.unknown;
        tally.provider = batch.provider;
        // Keep the first examples seen rather than the last: they are what the
        // operator started reading, and having them shuffle on every batch
        // makes the panel unreadable during a long sweep.
        tally.samples = [...tally.samples, ...batch.samples].slice(0, 25);
        tally.remaining = batch.remaining;

        remaining = batch.checked === 0 ? 0 : batch.remaining;
        setVerifyRun({ ...tally, running: remaining > 0 && !cancelVerify.current });
      }

      setVerifyRun({ ...tally, running: false, cancelled: cancelVerify.current });
      await load();
    } catch (e) {
      setError(e.message || 'Verification failed');
      setVerifyRun({ ...tally, running: false, failed: true });
    } finally {
      setVerifying(false);
    }
  };

  const applySuggestion = async (contact) => {
    const suggestion = contact.verification?.suggestion;
    if (!suggestion) return;
    if (!window.confirm(
      `Change ${contact.email} to ${suggestion}?\n\nOnly do this if the correction is obviously right — a wrong guess emails a real stranger at the corrected address.`
    )) return;
    try {
      await apiApplySuggestion(jwtToken, contact._id);
      await load();
    } catch (e) { setError(e.message); }
  };

  const suppress = async (contact) => {
    if (!window.confirm(`Stop all email to ${contact.email}? They will be removed from every sequence.`)) return;
    try {
      await apiSuppressContact(jwtToken, contact._id);
      await load();
    } catch (e) { setError(e.message); }
  };

  const remove = async (contact) => {
    if (!window.confirm(`Remove ${contact.email} from the contact list?\n\nTheir history and unsubscribe status are kept, so they can never be accidentally re-mailed.`)) return;
    try {
      await apiDeleteContact(jwtToken, contact._id);
      await load();
    } catch (e) { setError(e.message); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) analyse(dropped);
  };

  return (
    <div>
      {stats && (
        <div className={styles.statGrid}>
          <Stat value={stats.total} label="Total contacts" />
          <Stat value={stats.active} label="Mailable" tone="success" />
          <Stat value={stats.replied} label="Replied" tone="success" />
          <Stat value={stats.bounced} label="Bounced" tone={stats.bounced ? 'danger' : undefined} />
          <Stat value={stats.unsubscribed} label="Unsubscribed" />
        </div>
      )}

      {error && <div className={styles.dangerBox}>{error}</div>}

      {/* ── Verification ────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 className="card-title" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={17} /> Verify addresses
        </h3>
        <p className="text-sm text-muted" style={{ marginBottom: 16, lineHeight: 1.6, maxWidth: 720 }}>
          Checks each address for sound syntax and, crucially, whether its domain actually
          accepts mail. A domain with no mail server is a guaranteed hard bounce, and bounces
          are what convince Gmail to stop delivering the rest of your campaign.
          {verifyStats?.provider
            ? ` Mailbox-level checks are on via ${verifyStats.provider}.`
            : ' Mailbox-level checks (does this exact inbox exist?) need a paid verifier — see MAILER-SETUP.md.'}
        </p>

        {verifyStats && (
          <div className={styles.statGrid} style={{ marginBottom: 16 }}>
            <Stat value={verifyStats.valid} label="Verified good" tone="success" />
            <Stat value={verifyStats.risky} label="Risky" />
            <Stat
              value={verifyStats.invalid}
              label="Undeliverable"
              tone={verifyStats.invalid ? 'danger' : undefined}
            />
            <Stat value={verifyStats.unknown} label="Couldn’t check" />
            <Stat value={verifyStats.unverified} label="Not yet checked" />
          </div>
        )}

        {verifyStats?.invalid > 0 && (
          <div className={styles.warnBox}>
            <div className={styles.boxTitle}>
              <AlertTriangle size={16} /> {verifyStats.invalid} address
              {verifyStats.invalid > 1 ? 'es' : ''} cannot receive mail
            </div>
            These are held back from new campaign enrolments automatically, and skipped if one is
            already enrolled. Filter the table below to <strong>Undeliverable</strong> to see them.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={() => runVerification('unverified')}
            disabled={verifying || !verifyStats?.unverified}
          >
            {verifying ? 'Verifying…' : `Verify ${verifyStats?.unverified || 0} unchecked`}
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => runVerification('problems')}
            disabled={verifying || !(verifyStats?.unknown || verifyStats?.risky)}
            title="Re-check the ones that timed out or came back risky"
          >
            Re-check {(verifyStats?.unknown || 0) + (verifyStats?.risky || 0)} unresolved
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => runVerification('all')}
            disabled={verifying}
            title="Re-check every contact from scratch"
          >
            Re-verify everything
          </button>
          {verifying && (
            <button className="btn btn-ghost" onClick={() => { cancelVerify.current = true; }}>
              Stop
            </button>
          )}
        </div>

        {verifyRun && (
          <div className={styles.infoBox} style={{ marginTop: 16 }}>
            <div className={styles.boxTitle}>
              {verifyRun.running
                ? <><span className="spinner" style={{ width: 15, height: 15 }} /> Checking…</>
                : verifyRun.failed
                  ? <><AlertTriangle size={16} /> Verification stopped early</>
                  : <><CheckCircle size={16} color="var(--success)" /> Checked {verifyRun.checked} addresses</>}
            </div>
            <div style={{ marginBottom: verifyRun.samples?.length ? 10 : 0 }}>
              {verifyRun.valid} good · {verifyRun.risky} risky ·{' '}
              <strong>{verifyRun.invalid} undeliverable</strong> · {verifyRun.unknown} couldn’t check
              {verifyRun.running && verifyRun.remaining > 0 && ` · ${verifyRun.remaining} to go`}
              {verifyRun.cancelled && ' · stopped early, progress kept'}
            </div>

            {verifyRun.samples?.length > 0 && (
              <div className={styles.tableWrap} style={{ maxHeight: 260, overflowY: 'auto' }}>
                <table className={styles.table}>
                  <thead>
                    <tr><th>Address</th><th>Verdict</th><th>Why</th></tr>
                  </thead>
                  <tbody>
                    {verifyRun.samples.map((s) => (
                      <tr key={s.email}>
                        <td className={styles.mono}>{s.email}</td>
                        <td><VerifyBadge verification={{ result: s.result }} /></td>
                        <td className={styles.cellMuted}>
                          {s.explanation}
                          {s.suggestion && <> Did you mean <strong>{s.suggestion}</strong>?</>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Import ──────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 className="card-title" style={{ marginBottom: 16 }}>Import a CSV</h3>

        {imported && (
          <div className={styles.infoBox}>
            <div className={styles.boxTitle}>
              <CheckCircle size={16} color="var(--success)" /> Imported {imported.imported} new contacts
            </div>
            {imported.updated > 0 && <div>{imported.updated} existing contacts were updated with new details.</div>}
            {imported.suppressed > 0 && (
              <div>{imported.suppressed} were skipped because they previously unsubscribed or bounced.</div>
            )}
          </div>
        )}

        {!dryRun && (
          <>
            <div style={{ marginBottom: 14, maxWidth: 340 }}>
              <Field label="List name" help="Groups this upload so a campaign can target it later.">
                <input
                  className="input"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder="e.g. Authors Q3"
                />
              </Field>
            </div>

            <div
              className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <Upload size={30} color="var(--text-muted)" style={{ margin: '0 auto 10px' }} />
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                Drop a CSV here, or choose a file
              </div>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => e.target.files[0] && analyse(e.target.files[0])}
                style={{ fontSize: 13 }}
                disabled={importing}
              />
              <p className="text-sm text-muted" style={{ marginTop: 10, lineHeight: 1.6 }}>
                Needs an email column. First name, last name, company, title, phone and website are
                detected automatically; every other column is kept and usable as a merge tag.
              </p>
            </div>
          </>
        )}

        {importing && !dryRun && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="spinner" style={{ width: 18, height: 18 }} />
            <span className="text-sm text-muted">Reading the file…</span>
          </div>
        )}

        {/* Dry-run summary — what *would* happen, before anything is written. */}
        {dryRun && (
          <div>
            <div className={styles.infoBox}>
              <div className={styles.boxTitle}><FileText size={16} /> {file?.name}</div>
              <div>
                <strong>{dryRun.imported}</strong> new contacts will be added
                {dryRun.updated > 0 && <>, <strong>{dryRun.updated}</strong> existing ones updated</>}.
              </div>
            </div>

            <div className={styles.statGrid} style={{ marginBottom: 16 }}>
              <Stat value={dryRun.imported} label="New contacts" tone="success" />
              <Stat value={dryRun.duplicatesInFile} label="Duplicates in file" />
              <Stat value={dryRun.alreadyExisted} label="Already on file" />
              <Stat value={dryRun.invalidEmails} label="Invalid addresses" tone={dryRun.invalidEmails ? 'danger' : undefined} />
              <Stat value={dryRun.deadDomains} label="Dead domains" tone={dryRun.deadDomains ? 'danger' : undefined} />
              <Stat value={dryRun.suppressed} label="Suppressed (kept out)" />
            </div>

            {dryRun.deadDomains > 0 && (
              <div className={styles.warnBox}>
                <div className={styles.boxTitle}>
                  <AlertTriangle size={16} /> {dryRun.deadDomains} address
                  {dryRun.deadDomains > 1 ? 'es' : ''} on domains that accept no mail
                </div>
                {dryRun.deadDomainSamples.slice(0, 5).join(', ')}
                {dryRun.deadDomainSamples.length > 5 && '…'}
                <div style={{ marginTop: 6 }}>
                  Each one is a hard bounce if mailed. They will still be imported so the record is
                  kept, but marked undeliverable and held out of campaigns.
                </div>
              </div>
            )}

            {dryRun.typoSamples?.length > 0 && (
              <div className={styles.warnBox}>
                <div className={styles.boxTitle}>
                  <Wand2 size={16} /> {dryRun.typoSamples.length} possible typo
                  {dryRun.typoSamples.length > 1 ? 's' : ''}
                </div>
                {dryRun.typoSamples.slice(0, 5).map((t) => (
                  <div key={t.email} className={styles.mono}>{t.email} → {t.suggestion}</div>
                ))}
                <div style={{ marginTop: 6 }}>
                  Nothing is corrected automatically — a wrong guess emails a real stranger. Fix them
                  in the table after importing.
                </div>
              </div>
            )}

            {dryRun.roleAccounts > 0 && (
              <div className={styles.warnBox}>
                <div className={styles.boxTitle}>
                  <AlertTriangle size={16} /> {dryRun.roleAccounts} role account{dryRun.roleAccounts > 1 ? 's' : ''} found
                </div>
                Addresses like {dryRun.roleAccountSamples.slice(0, 3).join(', ')} reach a shared team
                inbox rather than a person. They reply far less often and get reported as spam far more
                often. They will be imported and tagged <code>role-account</code>, so you can exclude
                them when enrolling a campaign.
              </div>
            )}

            {dryRun.invalidEmails > 0 && (
              <div className={styles.warnBox}>
                <div className={styles.boxTitle}><AlertTriangle size={16} /> {dryRun.invalidEmails} unusable address{dryRun.invalidEmails > 1 ? 'es' : ''}</div>
                Skipped: {dryRun.invalidSamples.slice(0, 5).join(', ')}
                {dryRun.invalidSamples.length > 5 && '…'}
              </div>
            )}

            {dryRun.customColumns?.length > 0 && (
              <div className={styles.infoBox}>
                <div className={styles.boxTitle}>Extra columns kept as merge tags</div>
                {dryRun.customColumns.map((c) => (
                  <code key={c} className={styles.mono} style={{ marginRight: 6 }}>{`{{${c}}}`}</code>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={commit} disabled={importing || !dryRun.imported}>
                {importing ? 'Importing…' : `Import ${dryRun.imported} contacts`}
              </button>
              <button className="btn btn-ghost" onClick={cancelImport} disabled={importing}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── List ────────────────────────────────────────────────────────── */}
      <div className={styles.toolbar}>
        <form onSubmit={submitSearch} style={{ display: 'flex', gap: 8, minWidth: 330 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: 12, color: 'var(--text-muted)' }} />
          <input
            className="input"
            style={{ paddingLeft: 34 }}
            placeholder="Search name, email, phone or company"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          </div>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>
        <select
          className="input"
          style={{ width: 'auto' }}
          value={filters.status}
          onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}
        >
          <option value="all">All statuses</option>
          <option value="active">Mailable</option>
          <option value="replied">Replied</option>
          <option value="completed">Sequence finished</option>
          <option value="bounced">Bounced</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
        <select
          className="input"
          style={{ width: 'auto' }}
          value={filters.verification}
          onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, verification: e.target.value })); }}
        >
          <option value="all">Any verification</option>
          <option value="valid">Verified good</option>
          <option value="risky">Risky</option>
          <option value="invalid">Undeliverable</option>
          <option value="unknown">Couldn’t check</option>
          <option value="unverified">Not yet checked</option>
        </select>
        <select
          className="input"
          style={{ width: 'auto' }}
          value={filters.list}
          onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, list: e.target.value })); }}
        >
          <option value="">All lists</option>
          {lists.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className={styles.spacer} />
        <span className="text-sm text-muted">{total} contact{total === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div className="empty"><span className="spinner" style={{ width: 30, height: 30 }} /></div>
      ) : contacts.length === 0 ? (
        <Empty icon={<Upload size={40} color="var(--text-muted)" />} title="No contacts yet">
          Import a CSV above to get started.
        </Empty>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Email</th><th>Phone</th><th>Name</th><th>Company</th><th>List</th>
                  <th>Status</th><th>Address</th><th>Last contacted</th><th />
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c._id}>
                    <td>{c.email}</td>
                    <td>{c.phone || <span className={styles.cellMuted}>—</span>}</td>
                    <td>{[c.firstName, c.lastName].filter(Boolean).join(' ') || <span className={styles.cellMuted}>—</span>}</td>
                    <td>{c.company || <span className={styles.cellMuted}>—</span>}</td>
                    <td className={styles.cellMuted}>{c.listName || '—'}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td><VerifyBadge verification={c.verification} /></td>
                    <td className={styles.cellMuted}>{formatDate(c.lastContactedAt)}</td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {c.verification?.suggestion && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => applySuggestion(c)}
                          title={`Looks like a typo — correct to ${c.verification.suggestion}`}
                        >
                          <Wand2 size={14} />
                        </button>
                      )}
                      {c.status === 'active' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => suppress(c)} title="Never email this contact again">
                          <Ban size={14} />
                        </button>
                      )}
                      <button className="btn btn-ghost btn-sm" onClick={() => remove(c)} title="Remove from list">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', marginTop: 16 }}>
              <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span className="text-sm text-muted">Page {page} of {pages}</span>
              <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContactsTab;
